import { Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { UUID } from 'angular2-uuid';
import { StoredStateData } from 'src/app/driver/storage/models/stored-state-data';
import { StoredCachedService } from 'src/app/driver/storage/models/stored-cached-service';
import { UserStateService } from 'src/app/user-state/user-state.service';
import { LoggerService } from 'src/app/telemetry/logger.service';
import { TelemetryIdentifiers } from 'src/app/telemetry/telemetry-identifiers';
import { RaceTolerantInsertOutcome } from 'src/app/cache/race-tolerant-insert-outcome.enum';
import { StateDataMapper } from 'src/app/driver/storage/mappers/dispatched-service-state-data-mapper';
import { CachedServiceRepositoryCacheManager } from '../../cache-manager/cached-service-repository-cache-manager';
import { StateDataRepositoryCacheManager } from '../../cache-manager/state-data-repository-cache-manager';

/**
 * Operation that retrieves state data for a service by ID, rebuilding it from the cache row if it is absent.
 *
 * If state data is found it is returned directly. If it is absent (a forward orphan — cache row exists but
 * state row does not), state is rebuilt from the cache row with default workflow dates, inserted
 * race-tolerantly, and the rebuilt row is returned. Returns null only when no cache row exists for the ID.
 */
@Injectable({
	providedIn: 'root'
})
export class GetOrRebuildStateDataByIdOperation {
	/**
	 * @constructor
	 * @param cachedServiceRepositoryCacheManager Cache manager used to look up the cache row for orphan rebuild.
	 * @param stateDataRepositoryCacheManager Cache manager used to interact with the StateDataRepository.
	 * @param userStateService Service to access the current user's state.
	 * @param logger Telemetry logger for orphan-rebuild visibility.
	 */
	constructor(
		private readonly cachedServiceRepositoryCacheManager: CachedServiceRepositoryCacheManager,
		private readonly stateDataRepositoryCacheManager: StateDataRepositoryCacheManager,
		private readonly userStateService: UserStateService,
		private readonly logger: LoggerService,
		private readonly stateDataMapper: StateDataMapper
	) { }

	/**
	 * Returns state data for the given service ID, rebuilding it from the cache row if the state row is absent.
	 * @param id The ID of the service to retrieve state data for.
	 * @returns Observable emitting the state data, or null if no cache row exists for the ID.
	 */
	public Execute$(id: UUID): Observable<StoredStateData | null> {
		return this.stateDataRepositoryCacheManager.GetByIndex$({
			Id: id,
			UserName: this.userStateService.State.Username!,
			CrownId: this.userStateService.State.CrownIdentifier!
		}).pipe(
			map((items: StoredStateData[]) => items.length > 0 ? items[0] : null),
			switchMap((existing: StoredStateData | null) => {
				if (existing !== null) {
					return of(existing);
				}
				return this.RebuildFromCache$(id);
			})
		);
	}

	/**
	 * Attempts to rebuild state from the cache row for the given service ID.
	 * @param id The ID of the service whose state row is missing.
	 * @returns Observable emitting the rebuilt state data, or null if no cache row exists.
	 */
	private RebuildFromCache$(id: UUID): Observable<StoredStateData | null> {
		return forkJoin([
			this.cachedServiceRepositoryCacheManager.GetById$(id),
			this.stateDataRepositoryCacheManager.GetAll$()
		]).pipe(
			switchMap(([cached, allState]: [StoredCachedService | null, StoredStateData[]]) => {
				if (cached === null) {
					return of(null);
				}
				const maxSequence: number = allState.reduce((max: number, storedStateData: StoredStateData) => Math.max(max, storedStateData.Sequence ?? 0), 0);
				const rebuilt: StoredStateData = this.stateDataMapper.ToDefaultStateData(cached, maxSequence + 1, this.userStateService.State.Username!, this.userStateService.State.CrownIdentifier!);
				this.logger.TrackEvent(TelemetryIdentifiers.EVENT_ORPHANED_STATE_REBUILT, { ServiceId: id, LegacyId: String(cached.LegacyId) });
				return this.stateDataRepositoryCacheManager.InsertToleratingRace$(rebuilt).pipe(
					tap((outcome: RaceTolerantInsertOutcome) => {
						if (outcome === RaceTolerantInsertOutcome.AlreadyPresent) {
							this.logger.TrackEvent(TelemetryIdentifiers.EVENT_ORPHANED_STATE_REBUILD_RACE_LOST, { ServiceId: id });
						}
					}),
					map(() => rebuilt)
				);
			})
		);
	}
}
